import ReactPixel from 'react-facebook-pixel';
import extend from 'lodash/extend';

const MP_VIEWED_MARKETING_PAGE_EVENT_NAME = "Viewed marketing page";
const MP_VIEWED_LANDING_PAGE_EVENT_NAME = "Viewed landing page";
const MP_CLICKED_GET_STARTED_EVENT_NAME = "Clicked Get Started";
const MP_CLICKED_WORK_1_TO_1_EVENT_NAME = "Clicked Work 1-to-1";
const MP_CLICKED_INVITE_TO_WORK_EVENT_NAME = "Clicked Invite to Work";
const MP_CLICKED_HOW_IT_WORKS_EVENT_NAME = "Clicked How It Works";
const MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME = "Clicked Free Design Consultation";
const MP_CLICKED_READ_REVIEWS_EVENT_NAME = "Clicked Read Our Reviews";
const MP_CLICKED_TESTIMONIALS_ARROW_EVENT_NAME = "Clicked testimonials arrow";
const MP_CLICKED_PRICING_FAQ_ITEM_EVENT_NAME = "Clicked pricing FAQ item";
const MP_CLICKED_BRIEF_FAQ_ITEM_EVENT_NAME = "Clicked brief FAQ item";
const MP_CLICKED_CROWD_FAQ_ITEM_EVENT_NAME = "Clicked crowd FAQ item";
const MP_CLICKED_WRAPUP_FAQ_ITEM_EVENT_NAME = "Clicked wrapup FAQ item";
const MP_CLICKED_1_TO_1_FAQ_ITEM_EVENT_NAME = "Clicked 1-to-1 FAQ item";
const MP_CHANGED_PAP_STEP_EVENT_NAME = "Changed PAP Step";
const MP_CHANGED_PAP_SECTION_EVENT_NAME = "Changed PAP Section";


/**
 * Tracks a Google Analytics event in production and logs to the console elsewhere.
 *
 * Supports an optional callback, which will be called once the his makes it to GA
 * or after a timeout of 1 second is exceeded.
 */
const trackGoogle = (category, action, label=null, value=null, callback=null) => {
  var gaOptions = {
    hitType: 'event',
    eventCategory: category,
    eventAction: action
  }

  if (label) {
    gaOptions['eventLabel'] = label;
  }

  if (value) {
    gaOptions['eventValue'] = value;
  }

  if (callback) {
    gaOptions['hitCallback'] = createFunctionWithTimeout(function() {
      callback();
    })
  }

  if (process.env.NODE_ENV === 'production') {
    if (window.ga) {
      window.ga('send', gaOptions);
    } else {
      console.log("Google Analytics library not initialised properly when sending the following event: " + category + " / " + action);
    }
  } else {
    console.log('Google Analytics event: ', gaOptions);
  }
}

const setGoogleDimension = (dimension, value) => {
  if (process.env.NODE_ENV === 'production') {
    if (window.ga) {
      window.ga('set', dimension, value);
    } else {
      console.log("Google Analytics library not initialised properly when setting the dimension: " + dimension + " / " + value);
    }
  } else {
    console.log('Google Analytics dimension: ', dimension, value);
  }
}

const trackMixpanel = (eventName, eventData) => {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    if (window.mixpanel) {
      window.mixpanel.track(eventName, eventData);
    } else {
      console.log("Mixpanel not initialised properly when sending the following event: " + eventName, eventData);
    }
  } else {
    console.log('Mixpanel event: ' + eventName, eventData);
  }
}

const mixpanelCrowdspringId = (userId) => {
  return `cs-${userId}`;
}

const identifyWithMixpanel = (userId) => {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    if (window.mixpanel && window.mixpanel !== undefined) {
      window.mixpanel.identify(mixpanelCrowdspringId(userId));
    }
  } else {
    console.log("Identifying with Mixpanel: ", userId);
  }
}

const deIdentifyWithMixpanel = () => {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    if (window.mixpanel && window.mixpanel !== undefined) {
      window.mixpanel.reset();
    }
  } else {
    console.log("De-Identifying with Mixpanel");
  }
}

const aliasWithMixpanel = (userId) => {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    if (window.mixpanel && window.mixpanel !== undefined) {
      window.mixpanel.alias(mixpanelCrowdspringId(userId));
    }
  } else {
    console.log("Aliasing previous ID with Mixpanel: ", userId);
  }
}

const trackClickWithMixpanel = (eventName, eventData) => {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    if (window.mixpanel) {
      window.mixpanel.track(eventName, eventData);
    } else {
      console.log("Mixpanel not initialised properly when sending the following event: " + eventName, eventData);
    }
  } else {
    console.log('Mixpanel event: ' + eventName, eventData);
  }
}

const trackChargeWithMixpanel = (value) => {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    if (window.mixpanel) {
      window.mixpanel.people.track_charge(value);
      window.mixpanel.people.increment("Lifetime Value", value);
      window.mixpanel.people.set("Last Purchase", new Date().toISOString());
    } else {
      console.log("Mixpanel not initialised properly when sending the following event: " + eventName, eventData);
    }
  } else {
    console.log('Mixpanel charge: ' + value);
  }
}

const setMixpanelProfile = (email, profileData, isLazyRegistered=null) => {
  let mixpanelData = {
    '$email': email
  };

  extend(mixpanelData, profileData)
  if (isLazyRegistered !== null) {
    mixpanelData['lazy_registered'] = isLazyRegistered;
  }

  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    if (window.mixpanel) {
      window.mixpanel.people.set(mixpanelData);
    } else {
      console.log("Mixpanel not initialised properly when setting profile data: ", mixpanelData);
    }
  } else {
    console.log('Mixpanel profile: ', mixpanelData);
  }
}

const mixpanelProfileFromCsProfile = (csProfile) => {
  return ({
    '$first_name': csProfile["first_name"],
    '$last_name': csProfile["last_name"],
    'username': csProfile["username"],
    'lazy_registered': csProfile["is_lazy_registered"],
    'is_client': csProfile["is_client"],
    'is_creative': csProfile["is_creative"],
    'lead_source': csProfile["lead_source"],
    'converted_from': csProfile["user_converted_from"]
  });
}

const trackPerfectAudience = (eventName, extraData = {}) => {
  if (process.env.NODE_ENV === 'production') {
    if (window._pq) {
      _pq.push(['track', eventName, extraData]);
    } else {
      console.log("Pefect Audience library not initialised properly when sending the following event: " + eventName);
    }
  } else {
    console.log('Perfect Audience event: ', eventName, extraData);
  }
}

const trackFacebookPurchase = (pixelID, projectID, value) => {
  if (process.env.NODE_ENV === 'production') {
    ReactPixel.init(pixelID);
    ReactPixel.pageView();
    ReactPixel.track("Purchase", {
      content_type: 'product',
      content_ids: [projectID],
      value: value,
      currency: 'USD'
    });
  } else {
    console.log('Facebook purchase: ', pixelID, projectID, value);
  }
}

const trackFacebookCustomEvent = (pixelID, eventName, extraData = {}) => {
  if (process.env.NODE_ENV === 'production') {
    ReactPixel.init(pixelID);
    ReactPixel.pageView();
    ReactPixel.trackCustom(eventName, extraData);
  } else {
    console.log('Facebook custom event: ', pixelID, eventName, extraData);
  }
}

const trackQuora = () => {
  if (process.env.NODE_ENV === 'production') {
    if (window.qp) {
      qp('track', 'Generic');
    } else {
      console.log("Quora library not initialised properly during conversion tracking");
    }
  } else {
    console.log('Quora purchase');
  }
}

const trackBing = (category, action, label=null, value=null) => {
  window.uetq = window.uetq || [];

  var bingOptions = {
    ec: category,
    ea: action
  }

  if (label) {
    bingOptions['el'] = label;
  }

  if (value) {
    bingOptions['ev'] = value;
    bingOptions['gv'] = value;
    bingOptions['gc'] = "USD";
  }

  if (process.env.NODE_ENV === 'production') {
    window.uetq.push(bingOptions);
  } else {
    console.log('Bing UET event: ', bingOptions);
  }
}

const trackAdWordsProjectPostedConversion = (projectId, value, callback) => {
  var adWordsOptions = {
    value: value,
    send_to: 'AW-1042074590/BRswCLehzH0Q3pfz8AM',
    currency: 'USD',
    transaction_id: projectId
  };

  if (callback) {
    adWordsOptions['event_callback'] = callback
  }

  if (process.env.NODE_ENV === 'production') {
    if (window.gtag) {
      window.gtag('event', 'conversion', adWordsOptions);
    } else {
      console.log("Google AdWords tracking not initialised properly");
    }
  } else {
    console.log("Google AdWords event: ", adWordsOptions);
  }
}


/**
 * A function that that returns another function, which will call the provided
 * callback if called within 1 second. If that doesn't happen the callback is called
 * anyway.
 */
function createFunctionWithTimeout(callback, opt_timeout) {
  var called = false;
  function fn() {
    if (!called) {
      called = true;
      callback();
    }
  }
  setTimeout(fn, opt_timeout || 1000);
  return fn;
}

export { trackGoogle, setGoogleDimension, trackPerfectAudience, trackFacebookPurchase,
  trackFacebookCustomEvent, trackBing, trackQuora, trackAdWordsProjectPostedConversion,
  trackMixpanel, identifyWithMixpanel, deIdentifyWithMixpanel, aliasWithMixpanel,
  trackClickWithMixpanel, trackChargeWithMixpanel, setMixpanelProfile, mixpanelProfileFromCsProfile,
  MP_VIEWED_MARKETING_PAGE_EVENT_NAME, MP_VIEWED_LANDING_PAGE_EVENT_NAME,
  MP_CLICKED_GET_STARTED_EVENT_NAME, MP_CLICKED_HOW_IT_WORKS_EVENT_NAME,
  MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME, MP_CLICKED_READ_REVIEWS_EVENT_NAME,
  MP_CLICKED_TESTIMONIALS_ARROW_EVENT_NAME, MP_CLICKED_WORK_1_TO_1_EVENT_NAME,
  MP_CLICKED_INVITE_TO_WORK_EVENT_NAME, MP_CLICKED_PRICING_FAQ_ITEM_EVENT_NAME,
  MP_CLICKED_1_TO_1_FAQ_ITEM_EVENT_NAME, MP_CHANGED_PAP_STEP_EVENT_NAME,
  MP_CHANGED_PAP_SECTION_EVENT_NAME,
  MP_CLICKED_WRAPUP_FAQ_ITEM_EVENT_NAME, MP_CLICKED_CROWD_FAQ_ITEM_EVENT_NAME,
  MP_CLICKED_BRIEF_FAQ_ITEM_EVENT_NAME
}



// WEBPACK FOOTER //
// ./common/tracking.jsx